import type { Emitter } from "@core/events/emitter";
import type { IAuthModel } from "./IAuthModel";
import type {
  AuthTokens,
  ConfirmResetPasswordRequest,
  ConfirmSignUpRequest,
  IdentityProvider,
  ResetPasswordRequest,
  SignInRequest,
  SignInResult,
  SignUpRequest,
  UserInfo,
} from "./types";

/**
 * `DevAutoLoginAuthModel` — TEMPORARY development convenience.
 *
 * Wraps the real {@link IAuthModel} (Amplify/Cognito) and, when no cached
 * session restores on boot, signs in with credentials from the developer's
 * git-ignored `.env.local` file.
 */
export class DevAutoLoginAuthModel implements IAuthModel {
  private m_initPromise: Promise<UserInfo | null> | null = null;

  constructor(
    private readonly m_inner: IAuthModel,
    private readonly m_credentials: SignInRequest,
  ) {}

  // ---- Delegated state ------------------------------------------------

  get isInitialized(): boolean {
    return this.m_inner.isInitialized;
  }

  get currentUser(): UserInfo | null {
    return this.m_inner.currentUser;
  }

  get identityProvider(): IdentityProvider {
    return this.m_inner.identityProvider;
  }

  get isAuthenticated(): boolean {
    return this.m_inner.isAuthenticated;
  }

  // ---- Delegated events -------------------------------------------------

  get onInitialized(): Emitter<void> {
    return this.m_inner.onInitialized;
  }

  get onSignedIn(): Emitter<UserInfo> {
    return this.m_inner.onSignedIn;
  }

  get onSignedOut(): Emitter<void> {
    return this.m_inner.onSignedOut;
  }

  get onTokensRefreshed(): Emitter<AuthTokens> {
    return this.m_inner.onTokensRefreshed;
  }

  get onSessionExpired(): Emitter<void> {
    return this.m_inner.onSessionExpired;
  }

  // ---- Lifecycle ---------------------------------------------------------

  /**
   * Restore the cached session; when none exists, auto-login with the dev
   * account. Deduped so the router guard and the bootstrap path don't race
   * two sign-ins.
   */
  initializeAsync(): Promise<UserInfo | null> {
    if (!this.m_initPromise) {
      this.m_initPromise = this.initializeWithAutoLoginAsync();
    }
    return this.m_initPromise;
  }

  ensureFreshTokens(): Promise<void> {
    return this.m_inner.ensureFreshTokens();
  }

  private async initializeWithAutoLoginAsync(): Promise<UserInfo | null> {
    const restored = await this.m_inner.initializeAsync();
    if (restored) return restored;

    try {
      const result = await this.m_inner.signInAsync(this.m_credentials);
      if (result.kind === "signedIn") return result.user;
      console.error(
        `[DevAutoLoginAuthModel] auto-login hit an unexpected challenge: ${result.kind}`,
      );
      return null;
    } catch (err) {
      console.error("[DevAutoLoginAuthModel] auto-login failed:", err);
      return null;
    }
  }

  // ---- Delegated commands -------------------------------------------------

  signInAsync(req: SignInRequest): Promise<SignInResult> {
    return this.m_inner.signInAsync(req);
  }

  async signOutAsync(): Promise<void> {
    // Allow explicit sign-out during the session; auto-login only re-arms
    // on the next full boot.
    await this.m_inner.signOutAsync();
  }

  signUpAsync(req: SignUpRequest): Promise<{ requiresConfirmation: boolean }> {
    return this.m_inner.signUpAsync(req);
  }

  confirmSignUpAsync(req: ConfirmSignUpRequest): Promise<void> {
    return this.m_inner.confirmSignUpAsync(req);
  }

  resendSignUpCodeAsync(email: string): Promise<void> {
    return this.m_inner.resendSignUpCodeAsync(email);
  }

  resetPasswordAsync(req: ResetPasswordRequest): Promise<void> {
    return this.m_inner.resetPasswordAsync(req);
  }

  confirmResetPasswordAsync(req: ConfirmResetPasswordRequest): Promise<void> {
    return this.m_inner.confirmResetPasswordAsync(req);
  }

  getIdToken(forceRefresh?: boolean): Promise<string | null> {
    return this.m_inner.getIdToken(forceRefresh);
  }
}
